import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Trophy, Instagram, ArrowRight, Star, Crown } from "lucide-react";
import { API } from "@/App";
import axios from "axios";

const LookFestWinnerShowcase = () => {
  const navigate = useNavigate();
  const [currentWinner, setCurrentWinner] = useState(null);
  const [loading, setLoading] = useState(true);

  // Placeholder winners for when no real winners exist yet
  const placeholderWinners = [
    {
      name: "February Winner",
      shop_name: "Could Be You!",
      location: "Your City, USA",
      image: "https://static.prod-images.emergentagent.com/jobs/675f906d-89d5-4d72-be4d-b52475a90edd/images/76dd764acda10b521fee7ccb7dc4252eda1b4868c519c799bf5d140e4835ba42.png",
      month: "February 2026",
      prize: "$100",
      placeholder: true
    }
  ];

  useEffect(() => {
    const fetchWinner = async () => {
      try {
        const response = await axios.get(`${API}/lookfest/current-winner`);
        if (response.data && response.data.winner) {
          // Map backend data to frontend expected format
          const winnerData = response.data.winner;
          setCurrentWinner({
            ...winnerData,
            location: winnerData.shop_location || "Location TBD",
            prize: `$${winnerData.prize_amount || 100}`,
            placeholder: false
          });
        } else {
          setCurrentWinner(placeholderWinners[0]);
        }
      } catch (error) {
        // Use placeholder if no winner endpoint or no winner yet
        setCurrentWinner(placeholderWinners[0]);
      }
      setLoading(false);
    };
    fetchWinner();
  }, []);

  if (loading) return null;

  const winner = currentWinner || placeholderWinners[0];

  return (
    <div className="py-16 px-4 bg-gradient-to-b from-amber-50 to-white">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-10">
          <Badge className="bg-amber-100 text-amber-700 border-amber-300 mb-4 px-4 py-2">
            <Trophy className="w-4 h-4 mr-2" />
            Keep the 100 Look Fest
          </Badge>
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-3">
            {winner.placeholder ? "Enter to Win" : "Monthly Winner Spotlight"}
          </h2>
          <p className="text-gray-600 text-lg">
            {winner.placeholder 
              ? "Our first contest winner could be YOU! Enter now."
              : `Congratulations to our ${winner.month} winner!`
            }
          </p>
        </div>

        {/* Winner Card */}
        <div className="max-w-4xl mx-auto">
          <Card className="overflow-hidden border-2 border-amber-200 shadow-xl bg-white">
            <div className="grid md:grid-cols-2">
              {/* Winner Image */}
              <div className="relative">
                <img 
                  src={winner.image} 
                  alt={winner.name}
                  className="w-full h-full object-cover min-h-[300px] md:min-h-[400px]"
                />
                <div className="absolute top-4 left-4">
                  <Badge className="bg-amber-500 text-white border-0 px-4 py-2 text-base shadow-lg">
                    <Trophy className="w-4 h-4 mr-2" />
                    {winner.month} {winner.placeholder ? "" : "Winner"}
                  </Badge>
                </div>
                {!winner.placeholder && (
                  <div className="absolute bottom-4 right-4">
                    <Badge className="bg-green-500 text-white border-0 px-4 py-2 text-lg font-bold shadow-lg">
                      Won {winner.prize}!
                    </Badge>
                  </div>
                )}
              </div>

              {/* Winner Info */}
              <CardContent className="p-8 flex flex-col justify-center">
                {winner.placeholder ? (
                  <>
                    <div className="mb-6">
                      <h3 className="text-2xl font-bold text-gray-900 mb-2">Your Spotlight Awaits</h3>
                      <p className="text-gray-600">
                        Win $100 cash + Premium access + get featured right here on our homepage!
                      </p>
                    </div>

                    <div className="space-y-4 mb-6">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                          <span className="text-green-600 font-bold">1</span>
                        </div>
                        <p className="text-gray-700">Register on MyHairChair</p>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                          <span className="text-green-600 font-bold">2</span>
                        </div>
                        <p className="text-gray-700">Post your looks to @myhairchairhq</p>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                          <span className="text-green-600 font-bold">3</span>
                        </div>
                        <p className="text-gray-700">Use #KeepThe100LookFest</p>
                      </div>
                    </div>

                    <Button
                      size="lg"
                      className="bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white font-bold"
                      onClick={() => navigate('/lookfest')}
                    >
                      <Trophy className="w-5 h-5 mr-2" />
                      Enter the Contest
                      <ArrowRight className="w-5 h-5 ml-2" />
                    </Button>
                  </>
                ) : (
                  <>
                    <div className="mb-6">
                      <h3 className="text-3xl font-bold text-gray-900 mb-2">{winner.name}</h3>
                      <p className="text-xl text-amber-600 font-medium">{winner.shop_name}</p>
                      <p className="text-gray-500">{winner.location}</p>
                    </div>

                    {winner.instagram_handle && (
                      <div className="flex items-center gap-2 mb-6 text-gray-600">
                        <Instagram className="w-5 h-5 text-pink-500" />
                        <span>{winner.instagram_handle}</span>
                      </div>
                    )}

                    <div className="bg-amber-50 rounded-xl p-4 mb-6">
                      <p className="text-amber-800 italic">
                        "{winner.testimonial || 'Keep the 100 changed my business. Zero fees means I keep every dollar I earn!'}"
                      </p>
                    </div>

                    <div className="flex gap-3">
                      <Button
                        className="bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white font-bold flex-1"
                        onClick={() => navigate('/lookfest')}
                      >
                        <Trophy className="w-5 h-5 mr-2" />
                        You Could Be Next!
                      </Button>
                    </div>
                  </>
                )}
              </CardContent>
            </div>
          </Card>
        </div>

        {/* Contest Stats */}
        <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-4 max-w-3xl mx-auto">
          <div className="text-center p-4 bg-white rounded-xl border border-gray-200 shadow-sm">
            <p className="text-2xl font-bold text-amber-500">$100</p>
            <p className="text-sm text-gray-500">Monthly Prize</p>
          </div>
          <div className="text-center p-4 bg-white rounded-xl border border-gray-200 shadow-sm">
            <p className="text-2xl font-bold text-amber-500">0%</p>
            <p className="text-sm text-gray-500">Platform Fees</p>
          </div>
          <div className="text-center p-4 bg-white rounded-xl border border-gray-200 shadow-sm">
            <p className="text-2xl font-bold text-amber-500">8</p>
            <p className="text-sm text-gray-500">Monthly Rounds</p>
          </div>
          <div className="text-center p-4 bg-white rounded-xl border border-gray-200 shadow-sm">
            <p className="text-2xl font-bold text-amber-500">$1,000</p>
            <p className="text-sm text-gray-500">Grand Prize</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LookFestWinnerShowcase;
